import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Container, Row, Col, Card, Form, Button, Badge, 
  InputGroup, Dropdown, Pagination, Alert, Spinner,
  Modal, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { promptsAPI, tagsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { 
  Search, Filter, Plus, Eye, Edit, Trash2, Copy, 
  Heart, Share2, MoreVertical, Grid, List, 
  Eye as EyeIcon, Users, Lock, Tag, Calendar, BarChart3
} from 'lucide-react';

const PromptsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [filters, setFilters] = useState({
    search: '',
    visibility: '',
    category: '',
    aiModel: '',
    tags: [],
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12
  });
  
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, promptId: null });

  // Fetch prompts with filters
  const { data: promptsData, isLoading, error, refetch } = useQuery(
    ['prompts', filters, pagination],
    () => promptsAPI.getPrompts({
      ...filters,
      page: pagination.page,
      limit: pagination.limit
    }),
    {
      keepPreviousData: true,
      staleTime: 30 * 1000, // 30 seconds
    }
  );

  // Fetch available tags for filter
  const { data: tagsData } = useQuery(
    ['tags', 'all'],
    () => tagsAPI.getTags({ limit: 100 }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch categories
  const { data: categoriesData } = useQuery(
    ['prompts', 'categories'],
    () => promptsAPI.getCategories(),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle prompt actions
  const handlePromptAction = async (action, promptId) => {
    try {
      switch (action) {
        case 'view':
          navigate(`/prompts/${promptId}`);
          break;
        case 'edit':
          navigate(`/prompts/${promptId}/edit`);
          break;
        case 'copy':
          await promptsAPI.copyPrompt(promptId);
          toast.success('Prompt in die Zwischenablage kopiert!');
          break;
        case 'like':
          await promptsAPI.likePrompt(promptId);
          toast.success('Prompt gefällt dir!');
          refetch();
          break;
        case 'delete':
          setDeleteModal({ show: true, promptId });
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedPrompts.map(id => promptsAPI.deletePrompt(id)));
      toast.success(`${selectedPrompts.length} Prompt(s) erfolgreich gelöscht!`);
      setSelectedPrompts([]);
      refetch();
    } catch (error) {
      toast.error('Fehler beim Löschen einiger Prompts');
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPrompts(promptsData?.data?.map(p => p.id) || []);
    } else {
      setSelectedPrompts([]);
    }
  };

  // Handle individual selection
  const handleSelectPrompt = (promptId, checked) => {
    if (checked) {
      setSelectedPrompts(prev => [...prev, promptId]);
    } else {
      setSelectedPrompts(prev => prev.filter(id => id !== promptId));
    }
  };

  // Get visibility icon and color
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <EyeIcon size={16} className="text-success" />;
      case 'team':
        return <Users size={16} className="text-info" />;
      case 'private':
        return <Lock size={16} className="text-secondary" />;
      default:
        return <Lock size={16} className="text-secondary" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Prompt card component
  const PromptCard = ({ prompt }) => {
    const isSelected = selectedPrompts.includes(prompt.id);
    const canEdit = user?.role === 'admin' || prompt.author?.id === user?.id;

    return (
      <Card className={`h-100 ${isSelected ? 'border-primary' : ''}`}>
        <Card.Header className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center gap-2">
            {selectedPrompts.length > 0 && (
              <Form.Check
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleSelectPrompt(prompt.id, e.target.checked)}
                className="me-2"
              />
            )}
            {getVisibilityIcon(prompt.visibility)}
            <small className="text-muted">
              {formatDate(prompt.createdAt)}
            </small>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="link" size="sm" className="p-0">
              <MoreVertical size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handlePromptAction('view', prompt.id)}>
                <Eye size={16} className="me-2" />
                Ansehen
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handlePromptAction('copy', prompt.id)}>
                <Copy size={16} className="me-2" />
                Kopieren
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handlePromptAction('like', prompt.id)}>
                <Heart size={16} className="me-2" />
                Gefällt mir
              </Dropdown.Item>
              {canEdit && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handlePromptAction('edit', prompt.id)}>
                    <Edit size={16} className="me-2" />
                    Bearbeiten
                  </Dropdown.Item>
                  <Dropdown.Item 
                    onClick={() => handlePromptAction('delete', prompt.id)}
                    className="text-danger"
                  >
                    <Trash2 size={16} className="me-2" />
                    Löschen
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Card.Header>
        
        <Card.Body className="d-flex flex-column">
          <h6 className="card-title mb-2 line-clamp-2">{prompt.title}</h6>
          
          {prompt.description && (
            <p className="card-text text-muted small mb-3 line-clamp-3">
              {prompt.description}
            </p>
          )}
          
          <div className="mb-3">
            {prompt.category && (
              <Badge bg="outline-secondary" className="me-1 mb-1">
                {prompt.category}
              </Badge>
            )}
            {prompt.isTemplate && (
              <Badge bg="warning" text="dark" className="me-1 mb-1">
                Vorlage
              </Badge>
            )}
          </div>
          
          {prompt.tags && prompt.tags.length > 0 && (
            <div className="mb-3">
              <div className="d-flex flex-wrap gap-1">
                {prompt.tags.slice(0, 3).map(tag => (
                  <Badge key={tag.id} bg="primary" className="small">
                    <Tag size={12} className="me-1" />
                    {tag.displayName}
                  </Badge>
                ))}
                {prompt.tags.length > 3 && (
                  <Badge bg="secondary" className="small">
                    +{prompt.tags.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-auto">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <BarChart3 size={14} className="text-muted" />
                <small className="text-muted">
                  {prompt.usage?.views || 0} Aufrufe
                </small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Heart size={14} className="text-muted" />
                <small className="text-muted">
                  {prompt.usage?.likes || 0}
                </small>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Prompt list item component
  const PromptListItem = ({ prompt }) => {
    const isSelected = selectedPrompts.includes(prompt.id);
    const canEdit = user?.role === 'admin' || prompt.author?.id === user?.id;

    return (
      <div className={`border rounded p-3 mb-3 ${isSelected ? 'border-primary bg-light' : ''}`}>
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            {selectedPrompts.length > 0 && (
              <Form.Check
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleSelectPrompt(prompt.id, e.target.checked)}
                className="me-2"
              />
            )}
            
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h6 className="mb-0">{prompt.title}</h6>
                {getVisibilityIcon(prompt.visibility)}
                {prompt.isTemplate && (
                  <Badge bg="warning" text="dark" className="small">
                    Vorlage
                  </Badge>
                )}
              </div>
              
              {prompt.description && (
                <p className="text-muted small mb-2">{prompt.description}</p>
              )}
              
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  <Calendar size={14} className="me-1" />
                  {formatDate(prompt.createdAt)}
                </small>
                <small className="text-muted">
                  <BarChart3 size={14} className="me-1" />
                  {prompt.usage?.views || 0} Aufrufe
                </small>
                <small className="text-muted">
                  <Heart size={14} className="me-1" />
                  {prompt.usage?.likes || 0} Likes
                </small>
              </div>
            </div>
          </div>
          
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handlePromptAction('view', prompt.id)}
            >
              <Eye size={16} />
            </Button>
            {canEdit && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handlePromptAction('edit', prompt.id)}
              >
                <Edit size={16} />
              </Button>
            )}
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                <MoreVertical size={16} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handlePromptAction('copy', prompt.id)}>
                  <Copy size={16} className="me-2" />
                  Kopieren
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handlePromptAction('like', prompt.id)}>
                  <Heart size={16} className="me-2" />
                  Gefällt mir
                </Dropdown.Item>
                {canEdit && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item 
                      onClick={() => handlePromptAction('delete', prompt.id)}
                      className="text-danger"
                    >
                      <Trash2 size={16} className="me-2" />
                      Löschen
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <Alert.Heading>Fehler beim Laden der Prompts</Alert.Heading>
          <p>{error.message}</p>
          <Button onClick={() => refetch()}>Erneut versuchen</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">Prompts</h1>
              <p className="text-muted mb-0">
                {promptsData?.total ? `${promptsData.total} Prompt(s) gefunden` : 'Keine Prompts gefunden'}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={16} className="me-2" />
                Filter
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              >
                {viewMode === 'grid' ? <List size={16} /> : <Grid size={16} />}
              </Button>
              <Button as="a" href="/prompts/create" variant="primary">
                <Plus size={16} className="me-2" />
                Prompt erstellen
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row>
                  <Col md={6}>
                    <InputGroup>
                      <InputGroup.Text>
                        <Search size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Prompts suchen..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                      <Button type="submit" variant="primary">
                        Suchen
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex gap-2">
                      <Form.Select
                        value={filters.visibility}
                        onChange={(e) => handleFilterChange('visibility', e.target.value)}
                        className="flex-grow-1"
                      >
                        <option value="">Alle Sichtbarkeiten</option>
                        <option value="public">Öffentlich</option>
                        <option value="private">Privat</option>
                        <option value="team">Team</option>
                      </Form.Select>
                      <Form.Select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        className="flex-grow-1"
                      >
                        <option value="">Alle Kategorien</option>
                        {categoriesData?.map(category => ( // Changed from categoriesData?.data?.map to categoriesData?.map
                          <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bulk Actions */}
      {selectedPrompts.length > 0 && (
        <Row className="mb-3">
          <Col>
            <Card className="border-warning">
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    {selectedPrompts.length} Prompt{selectedPrompts.length !== 1 ? 's' : ''} ausgewählt
                  </span>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 size={16} className="me-2" />
                      Ausgewählte löschen
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedPrompts([])}
                    >
                      Auswahl aufheben
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Prompts Grid/List */}
      <Row>
        <Col>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Prompts werden geladen...</p>
            </div>
          ) : promptsData?.data?.length > 0 ? (
            <>
              {viewMode === 'grid' ? (
                <Row>
                  {promptsData.data.map(prompt => (
                    <Col key={prompt.id} lg={4} md={6} className="mb-4">
                      <PromptCard prompt={prompt} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div>
                  {promptsData.data.map(prompt => (
                    <PromptListItem key={prompt.id} prompt={prompt} />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {promptsData.totalPages > 1 && (
                <Row className="mt-4">
                  <Col>
                    <div className="d-flex justify-content-center">
                      <Pagination>
                        <Pagination.First
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.page === 1}
                        />
                        <Pagination.Prev
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                        />
                        
                        {Array.from({ length: Math.min(5, promptsData.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === pagination.page}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}
                        
                        <Pagination.Next
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === promptsData.totalPages}
                        />
                        <Pagination.Last
                          onClick={() => handlePageChange(promptsData.totalPages)}
                          disabled={pagination.page === promptsData.totalPages}
                        />
                      </Pagination>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="fas fa-brain text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                <h5 className="text-muted">Keine Prompts gefunden</h5>
                <p className="text-muted">
                  {filters.search || filters.visibility || filters.category 
                    ? 'Versuche, deine Filter oder Suchbegriffe anzupassen.'
                    : 'Erstelle deinen ersten Prompt, um loszulegen!'
                  }
                </p>
                {!filters.search && !filters.visibility && !filters.category && (
                  <Button as="a" href="/prompts/create" variant="primary">
                    <Plus size={16} className="me-2" />
                    Erstelle deinen ersten Prompt
                  </Button>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, promptId: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Prompt löschen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bist du sicher, dass du diesen Prompt löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal({ show: false, promptId: null })}>
            Abbrechen
          </Button>
          <Button 
            variant="danger" 
            onClick={async () => {
              try {
                await promptsAPI.deletePrompt(deleteModal.promptId);
                toast.success('Prompt erfolgreich gelöscht!');
                setDeleteModal({ show: false, promptId: null });
                refetch();
              } catch (error) {
                toast.error('Fehler beim Löschen des Prompts');
              }
            }}
          >
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PromptsPage;